// src/components/CycleSettings/useCycleSettingsData.ts

import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootState, Dispatch } from '../../../store/store';
import CycleSettingsEnum from '../../../enums/cycle-setting.enum';
import { ChannelState } from '../../../enums/channel-state.enum';
import { IPostWeldSettings } from '../../../types/cycle-settings-types';

export const useCycleSettingsData = (cycleId: string) => {
  const dispatch = useDispatch<Dispatch>();
  const [localMode, setLocalMode] = useState<CycleSettingsEnum | null>(null);
  const isUpdatedMode = useRef(false);

  // Get data from Redux store
  const { settings, cycleDbSettings, channelsSettings } = useSelector(
    (state: RootState) => ({
      settings: state.cycles?.cycle?.settings as IPostWeldSettings,
      cycleDbSettings: state.cycles?.cycleDbSettings,
      channelsSettings: state.cycles?.cycleIoTChannelsSettings,
    }),
    shallowEqual,
  );

  // Data fetching function
  const fetchData = async () => {
    try {
      await dispatch.cycles.getOne(cycleId);
      const res = await dispatch.cycles.getDbCycleSettings(cycleId);
      await dispatch.cycles.getIoTCycleSettings(cycleId);
      await dispatch.cycles.getSettingsByMode({ id: cycleId, query: { name: res.mode } });

      if (!isUpdatedMode.current && res.mode) {
        setLocalMode(res.mode);
        isUpdatedMode.current = true;
      }
    } catch (error) {
      console.error('Failed to fetch cycle settings:', error);
    }
  };

  // Data fetching and management
  useEffect(() => {
    fetchData(); // Initial fetch
    const interval = setInterval(fetchData, 20000);

    return () => clearInterval(interval);
  }, [cycleId, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch.cycles.clearDbSettings();
      dispatch.cycles.clearSettingsMode();
      dispatch.cycles.clearCycleIoTChannelsSettings();
      isUpdatedMode.current = false;
    };
  }, [dispatch]);

  // Memoized values for rendering logic
  const isActiveCycle = useMemo(() => settings?.state?.value !== ChannelState.STOPPED, [settings]);
  const isLoading = useMemo(() => !localMode, [localMode]);
  const isShowContent = useMemo(() => !!localMode && !!cycleDbSettings?.mode, [localMode, cycleDbSettings]);
  const isShowChannelTable = useMemo(
    () => channelsSettings && cycleDbSettings?.mode && cycleDbSettings.mode !== CycleSettingsEnum.Recorder,
    [channelsSettings, cycleDbSettings],
  );

  return {
    localMode,
    setLocalMode,
    isActiveCycle,
    isLoading,
    isShowContent,
    isShowChannelTable,
    cycleDbSettings,
    channelsSettings,
    refreshData: fetchData,
  };
};
